import { collection, addDoc, collectionGroup, deleteDoc, doc, getDocs, limit, orderBy, query, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export async function logOrgSwitch(user, orgId) {
  if (!user?.uid || user?.role !== "superadmin") return;
  try {
    const ref = collection(db, "orgs", orgId, "audit");
    await addDoc(ref, {
      type: "org_switch",
      actorId: user.uid,
      actorEmail: user.email || "",
      orgId,
      createdAt: serverTimestamp(),
    });
  } catch {}
}

export async function logOrgActivity(user, orgId, type, payload = {}) {
  if (!user?.uid || user?.role === "investor") return;
  try {
    const ref = collection(db, "orgs", orgId, "activity");
    await addDoc(ref, {
      type,
      actorId: user.uid,
      actorEmail: user.email || "",
      orgId,
      createdAt: serverTimestamp(),
      ...payload,
    });
  } catch {}
}

export async function fetchOrgActivity(user, orgId, maxRows = 20) {
  if (!user?.uid) return [];
  try {
    const ref = collection(db, "orgs", orgId, "activity");
    const q = query(ref, orderBy("createdAt", "desc"), limit(maxRows));
    const snap = await getDocs(q);
    return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  } catch {
    return [];
  }
}

export async function fetchOrgAudit(user, orgId, maxRows = 20) {
  if (!user?.uid || user?.role !== "superadmin") return [];
  try {
    const ref = collection(db, "orgs", orgId, "audit");
    const q = query(ref, orderBy("createdAt", "desc"), limit(maxRows));
    const snap = await getDocs(q);
    return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  } catch {
    return [];
  }
}

export async function pruneOrgAudit(user, orgId, keepDays = 90) {
  if (!user?.uid || user?.role !== "superadmin") return;
  if (!keepDays || Number(keepDays) <= 0) return;
  try {
    const cutoff = Date.now() - Number(keepDays) * 24 * 3600 * 1000;
    const ref = collection(db, "orgs", orgId, "audit");
    const q = query(ref, orderBy("createdAt", "asc"), limit(200));
    const snap = await getDocs(q);
    const stale = snap.docs.filter((docSnap) => {
      const ts = docSnap.data()?.createdAt?.toDate?.()?.getTime?.();
      return ts && ts < cutoff;
    });
    await Promise.all(stale.map((docSnap) => deleteDoc(doc(db, "orgs", orgId, "audit", docSnap.id))));
  } catch {}
}

export async function fetchOrgAuditAll(user, maxRows = 50) {
  if (!user?.uid || user?.role !== "superadmin") return [];
  try {
    const ref = collectionGroup(db, "audit");
    const q = query(ref, orderBy("createdAt", "desc"), limit(maxRows));
    const snap = await getDocs(q);
    return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  } catch {
    return [];
  }
}

function toCsvValue(value) {
  const str = value == null ? "" : String(value);
  const escaped = str.replace(/"/g, '""');
  return `"${escaped}"`;
}

export function buildAuditCsv(rows = []) {
  const header = ["Actor", "Org", "Type", "Date"];
  const lines = rows.map((row) => [
    row.actorEmail || row.actorId || "",
    row.orgId || "",
    row.type || "",
    row.createdAt?.toDate?.().toISOString?.() || "",
  ]);
  return [header, ...lines].map((r) => r.map(toCsvValue).join(",")).join("\n");
}

export function exportAuditCsv(rows, orgId) {
  const csv = buildAuditCsv(rows);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `nia-audit-${orgId || "org"}.csv`;
  a.click();
}

export function buildActivityCsv(rows = []) {
  const header = ["Type", "Actor", "Org", "Date"];
  const lines = rows.map((row) => [
    row.type || "",
    row.actorEmail || row.actorId || "",
    row.orgId || "",
    row.createdAt?.toDate?.().toISOString?.() || "",
  ]);
  return [header, ...lines].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
}

export function exportActivityCsv(rows, orgId) {
  const csv = buildActivityCsv(rows);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `nia-activity-${orgId || "org"}.csv`;
  a.click();
}
